/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import {
    GridColumnVisibilityModel,
    GridPinnedColumnFields,
    GridColumnDimensions,
} from '@mui/x-data-grid-premium';
import { Product } from 'common/services/PIMService';
import {
    Polygon,
    Position,
} from 'common/components/google-map/GoogleMapComponent';
import { OperationInCountry } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { ReactElement } from 'react';
import { InventoryDetail } from './distributionInventory';
import { DataGridState } from './datagrid';
import { Address } from './address';

export interface Vendor {
    name: string;
    doingBusinessAs: string;
    id: number;
    typeId: string;
    additionalCost: number;
    vendorTypeId: string;
    operationInCountry: OperationInCountry;
    currency: string;
    approvalDate: string;
    statusId: string;
    website: string;
    phone: string;
    phoneCountry: string;
    notes: string | null;
    contact: string | null;
    termNet: string;
    created: string;
    updated: string;
    address: Address;
    billingAddress: Address;
    distributionCenters: DistributionCenter[];
    integration: Integration;
    inventoryRules: InventoryRule[];
}

export interface Integration {
    typeId: string;
    isIntegrated: boolean;
    vendorId: number;
    actionId: string;
    actionDetails: {
        system: string;
        instructions: string;
    };
}

export interface InventoryRuleItem {
    id: string;
    subPriceDiff: {
        id: string;
        display: string;
    };
    partThreshold: {
        id: string;
        display: string;
    };
    productSubCategory: {
        id: string;
        display: string;
    };
}

export interface InventoryRule {
    vendorId: number;
    ruleName: string;
    id: number;
    created: string;
    updated: string;
    rules: InventoryRuleItem[];
}

interface VendorIntegrationConfigOptions {
    client: string;
    hasMapping: boolean;
    distributorId: string;
    mappingRequired: boolean;
}

interface VendorIntegrationConfig {
    ORDERING: {
        [key: string]: VendorIntegrationConfigOptions;
    };
    PAYMENTS: {
        client: string;
        options: {
            hasMapping: boolean;
            distributorId: string;
            mappingRequired: boolean;
        };
    };
    INVENTORY: {
        [key: string]: VendorIntegrationConfigOptions;
    };
}

interface VendorIntegration {
    typeId: string;
    isIntegrated: boolean;
    vendorId: number;
    actionId: string;
    actionDetails: {
        system: string;
        instructions: string;
    };
    config: VendorIntegrationConfig;
    id: number;
}

interface DistributionCenter {
    title?: string;
    id: number;
    name?: string;
    latitude: number;
    longitude: number;
    contact: {
        name: string;
        phone: string;
        email: string;
    };
    vendor?: Vendor;
    vendorId?: number;
    vendorSku?: string;
    vendorBranchName: string;
    vendorBranchNumber: string;
    vendorDistributionCenterId: number;
    vendorBranchContactName?: string;
    vendorBranchContactPhone?: string;
    vendorBranchContactEmail?: string;
    vendorGroupNumber: string | null;
    vendorGroupName: string | null;
    vendorGroupRegion: string | null;
    vendorBranchRegion: string;
    vendorBranchPhone: string;
    vendorBranchFax: string;
    vendorBranchContactTitle: string | null;
    vendorCountry?: string;
    preferred?: boolean;
    tradeType?: string | string[];
    productCategoryId?: string;
    productSubCategoryId?: string;
    quantityAvailable?: number;
    quantityText?: string;
    skus?: InventoryDetail[];
    virtualId?: string;
    path?: string[];
    distance?: number;
    active: boolean;
    idxAddress: string;
    rawAddress: string;
    stdLastLine: string | null;
    stdPrimaryNumber: string;
    stdStreetName: string;
    stdStreetPreDirection: string;
    stdStreetPostDirection: string | null;
    stdStreetSuffix: string;
    stdSecondaryNumber: string;
    stdSecondaryDesignator: string;
    stdPostalCodePlus4: string;
    stdCounty: string;
    stdDeliveryLine: string;
    stdDeliveryLine2?: string;
    stdAddress: string;
    stdCity: string;
    stdRegion: string;
    stdPostalCode: string;
    stdCountry: string;
    rdi: string;
    ehub: boolean;
    geocoded: boolean;
    standardized: boolean;
    timezone: string;
    notes: string | null;
    inventory: {
        id: number;
        productId: number;
        qty: string;
        quantityAvailable: number;
    } | null;
    canFulfill: boolean | null;
    minQuantityAvailable: number | null;
    vendorBranchExtId: string | null;
    created: string;
    updated: string;
    configuration?: DistributionCenterConfiguration[];
    radius?: number;
}

export enum SHIPPING_RADIUS_CONFIG {
    Equipment = 'EQUIPMENT_SHIPPING_RADIUS',
    Part = 'PART_SHIPPING_RADIUS',
}

export interface DistributionCenterConfiguration {
    id: number;
    vendorDistributionCenterId: number;
    vendorId?: number;
    type: string;
    key: string;
    value: string;
    active: boolean;
    created: string;
    updated: string;
}

export interface DistributionCentersAndInventoryViewState {
    initialized: boolean;
    searchFilters: SearchFilter[];
    states: { value: string; label: string }[];
    selectedStates: { value: string; label: string }[];
    distinctVendors: { id: string; label: string; value: number[] }[];
    selectedDistinctVendors: { id: string; label: string; value: number[] }[];
    polygon: Polygon | null;
    zipcode: string;
    showInventory: boolean;
    distributionCenterForInventory: DistributionCenter | object;
    inventoryResults: InventoryGroupedDistributionCenter[];
    searchType: {
        id: FilterType;
        display: string;
    };
    searchText: string;
    activeId: number;
    columnOrdering: string[];
    columnVisibility: GridColumnVisibilityModel;
    pinnedColumns: GridPinnedColumnFields;
    columnDimensions: Record<string, GridColumnDimensions>;
    distanceFromZip: number | null;
}

interface SearchResultDistributionCenter {
    filterId: string;
    branchId: number;
    vendorId: number;
    branchNumber: string;
}

interface BaseFilter {
    isInventory?: boolean;
    id: FilterType | number;
    display: string;
    target: string;
    value?: SearchValue[FilterType];
}

interface SearchFilter {
    target: FilterType | InventoryFilterType;
    display: string;
    searchTerm: string | { id: string } | { id: string; productId: string };
    id: number;
    isInventory: boolean;
    term: string;
    product?: Product;
    productId?: number;
    filterBy?: string | number;
}

type FilterType =
    | 'LOCATION'
    | 'BRANCH_NUMBER'
    | 'SKU'
    | 'TRADE_TYPE'
    | 'TRADE_TYPE_INVENTORY'
    | 'TRADE_SUB_TYPE_INVENTORY'
    | 'PRODUCT_CATEGORY'
    | 'PRODUCT_SUB_CATEGORY'
    | 'QUANTITY';

type SearchValue = {
    LOCATION: { formattedAddress: string; components?: { state: string } };
    BRANCH_NUMBER: {
        branchNumber: string;
        vendorId?: number;
    };
    SKU: { id: string; productId?: string; product?: any };
    TRADE_TYPE: string;
    TRADE_TYPE_INVENTORY: { display: string };
    TRADE_SUB_TYPE_INVENTORY: { display: string };
    PRODUCT_CATEGORY: { display: string };
    PRODUCT_SUB_CATEGORY?: { display: string };
    QUANTITY: boolean;
};

type SearchParams = {
    type: FilterType;
    value: SearchValue[FilterType];
    radius?: number;
};

interface MultiSearchParams {
    filters: Array<SearchParams>;
    overrideFilters?: boolean;
    isRemovingFilter?: boolean;
}

interface InventoryGroupedDistributionCenter {
    distributionCenterId: number;
    inventory: InventoryDetail[];
}

type DistributionCenterTradeTypes =
    | (
          | string
          | {
                id: string;
                productId?: string;
            }
      )[]
    | string[];

// eslint-disable-next-line no-undef
export interface DistributionAndInventoryMapMarker extends google.maps.Marker {
    hasInfoWindow?: boolean;
    workOrderData?: string | undefined;
    onClickLink?: () => void;
    id: number;
    position?: Position;
    title?: string;
    onClick?: () => void;
    infoWindow?: {
        eventListeners?: Array<{
            id: string;
            type: string;
            listener: () => void;
        }>;
        content: string;
    };
    vendor?: string;
    vendorId?: number;
    stdDeliveryLine?: string;
    stdCity?: string;
    stdRegion?: string;
    stdPostalCode?: string;
    stdCountry?: string;
    tradeType?: string;
    icon?: { url: string } | null;
    customIcon?: ReactElement | null;
    customIconSelected?: ReactElement | null;
    contractor?: {
        id: string;
        title: string;
    };
    contact?: {
        name?: string;
        phone?: string;
        email?: string;
    };
    onTop?: boolean;
    vendorBranchNumber: string;
    quantityAvailable?: number;
    quantityText?: string;
    distance?: number;
}

export const INVENTORY_FILTER_MAPPING = {
    SKU: { property: 'vendorSku' },
    TRADE_TYPE_INVENTORY: { property: 'tradeTypeId' },
    TRADE_SUB_TYPE_INVENTORY: { property: 'tradeSubTypeId' },
    PRODUCT_CATEGORY: { property: 'productCategoryId' },
    PRODUCT_SUB_CATEGORY: { property: 'productSubCategoryId' },
    QUANTITY: { property: 'quantity' },
} as const;

type InventoryFilterType = keyof typeof INVENTORY_FILTER_MAPPING;

type DistributionCentersAndInventoryView = DataGridState<DistributionCenter> &
    DistributionCentersAndInventoryViewState;

export type {
    SearchValue,
    DistributionCenter,
    SearchResultDistributionCenter,
    SearchParams,
    MultiSearchParams,
    FilterType,
    BaseFilter,
    InventoryFilterType,
    SearchFilter,
    InventoryGroupedDistributionCenter,
    VendorIntegration,
    VendorIntegrationConfig,
    DistributionCentersAndInventoryView,
    DistributionCenterTradeTypes,
};
